import { useState, useEffect } from "react";
import QRCode from "react-qr-code";

import {
  Box,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Switch,
  useTheme
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinkOffIcon from "@mui/icons-material/LinkOff";

import { truncateText } from "@aclymatepackages/other-helpers";

import Link from "../../atoms/mui/Link";
import GrayPaper from "../../atoms/GrayPaper";

import ProductDetailsSlider from "../../modules/products/ProductDetailsSlider";
import { exportProductsGapWorkbook } from "./bom-bulk-upload/bomGapExport";
import DataSharingTab from "../dashboard/views/vendors/DataSharingTab";

import DbAutocomplete from "../../inputs/autocomplete/DbAutocomplete";
import NewVendorForm from "../../inputs/vendors/NewVendorForm";

import {
  useCachedFirebaseCrud,
  useAccountData,
  useCachedDisplayData,
  firebaseFieldDeleteObj
} from "../../../helpers/firebase";
import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";
import { ProductFootprintLeftContent } from "../../modules/products/ProductFootprintLeftContent";
import useEmissionsContext from "../../../helpers/contexts/emissions";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import { fetchVendorCompanyData } from "../../../helpers/utils/apiCalls";
import { isProductLinkedToVendor } from "../../../helpers/components/products";
import { calculateMergedProductFootprint } from "@aclymatepackages/products";
import usePermissions from "../../../helpers/hooks/usePermissions";

const WebsiteTab = ({
  id: productId,
  creatorId,
  isPublic: originalIsPublic
}) => {
  const { shadows, palette } = useTheme();
  const [companyData] = useAccountData();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [{ isSubscribed }] = useProductsEmissionsFactors();

  const [isPublic, setIsPublic] = useState(originalIsPublic);
  const [qrCodeColor, setQrCodeColor] = useState("#000000");
  const { branding } = companyData || {};
  const { primaryColor, secondaryColor } = branding || {};

  const onPublicToggle = async (newValue) => {
    setIsPublic(newValue);
    return await updateCollectionDoc("product-components", productId, {
      isPublic: newValue
    });
  };

  const colorOptions = [
    { label: "black", value: "#000000" },
    { label: "white", value: "#FFFFFF" },
    {
      label: "brand-primary",
      value: primaryColor || palette.primary.main,
      tooltip: !primaryColor && "Set a primary color in your company branding."
    },
    ...(secondaryColor
      ? [
          {
            label: "brand-secondary",
            value: secondaryColor
          }
        ]
      : [])
  ];

  const exportQRCode = () => {
    const svg = document.querySelector("#qr-code-svg");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = 256;
    canvas.height = 256;

    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    img.onload = () => {
      ctx.drawImage(img, 0, 0);

      const { label: colorLabel } =
        colorOptions.find(({ value }) => value === qrCodeColor) ?? {};

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `product-qr-code-${productId}-${colorLabel}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
      });
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };
  const envParamsString =
    process.env.REACT_APP_ENVIRONMENT !== "production"
      ? `?env=${process.env.REACT_APP_ENVIRONMENT}`
      : "";
  const websiteUrl = `https://netzero.aclymate.com/products/${creatorId}/${productId}${envParamsString}`;

  return (
    <Grid container direction="column" spacing={2}>
      <Grid
        item
        container
        spacing={4}
        justifyContent="space-between"
        flexWrap="nowrap"
        alignItems="center"
      >
        <Grid item>
          <Typography variant="h6">Public Product Page</Typography>
          <Typography variant="body2" color="textSecondary">
            {isPublic
              ? "Your product's website is live and ready to share with customers and stakeholders."
              : "Turn on your product's public facing sustainability page to share it with customers and stakeholders."}
          </Typography>
        </Grid>
        <Grid item>
          <Tooltip
            title={
              !isSubscribed ? "Subscribe to enable public product pages" : ""
            }
            disableHoverListener={isSubscribed}
          >
            <span>
              <Switch
                disabled={!isSubscribed}
                checked={isPublic}
                onChange={(e) => onPublicToggle(e.target.checked)}
                color="primary"
              />
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Grid item container justifyContent="center">
        <Grid item>
          <GrayPaper>
            <Grid container direction="column" spacing={2}>
              <Grid
                item
                container
                spacing={4}
                justifyContent="space-between"
                flexWrap="nowrap"
                alignItems="center"
              >
                <Grid item>
                  <Typography variant="caption" display="block">
                    {truncateText(websiteUrl, 50)}
                  </Typography>
                </Grid>
                <Grid item>
                  <IconButton disabled={!isPublic}>
                    <Link href={websiteUrl}>
                      <LaunchIcon
                        fontSize="small"
                        style={{
                          color: isPublic
                            ? palette.text.primary
                            : palette.text.secondary
                        }}
                      />
                    </Link>
                  </IconButton>
                </Grid>
              </Grid>
              <Grid item container justifyContent="center">
                <Grid item style={{ filter: isPublic ? "none" : "blur(8px)" }}>
                  <QRCode
                    id="qr-code-svg"
                    value={websiteUrl}
                    fgColor={qrCodeColor}
                    bgColor="transparent"
                  />
                </Grid>
              </Grid>
              <Grid item container justifyContent="center">
                {colorOptions.map(({ value, tooltip }) => (
                  <Grid item key={value}>
                    <Tooltip title={tooltip} disableHoverListener={!tooltip}>
                      <IconButton
                        size="small"
                        onClick={() => setQrCodeColor(value)}
                      >
                        <Box
                          style={{
                            backgroundColor: value,
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            opacity: qrCodeColor === value ? 1 : 0.5,
                            boxShadow:
                              qrCodeColor === value ? shadows[5] : "none"
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
              <Grid item container justifyContent="center">
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportQRCode}
                  disabled={!isPublic}
                >
                  Export QR Code
                </Button>
              </Grid>
            </Grid>
          </GrayPaper>
        </Grid>
      </Grid>
    </Grid>
  );
};

const VendorTab = ({
  productVendor,
  setProductVendor,
  onRemoveVendor,
  productId,
  setSelectedVendor
}) => {
  const { name: vendorName, id: productVendorId } = productVendor || {};

  const { vendors } = useEmissionsContext();
  const [productLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );
  const [{ emissionsFactors }] = useProductsEmissionsFactors();

  const resolvedVendorRelDoc =
    vendors.find(({ id }) => id === productVendorId) ||
    vendors.find(({ vendorId }) => vendorId === productVendorId);
  const relationshipId = resolvedVendorRelDoc?.id;
  const vendorId = resolvedVendorRelDoc?.vendorId;

  const [editVendor, setEditVendor] = useState(!productVendor);
  const [showNewVendorForm, setShowNewVendorForm] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [vendorCompanyData, setVendorCompanyData] = useState({});

  const {
    isStripeCustomer,
    vendorSurveys,
    contactIndividuals = [],
    vendorStartDate
  } = vendorCompanyData;

  useEffect(() => {
    if (!vendorId || editVendor) {
      return;
    }
    setIsCheckingSubscription(true);
    fetchVendorCompanyData(vendorId, relationshipId).then((data) => {
      setVendorCompanyData(data || {});
      setIsCheckingSubscription(false);
    });
  }, [vendorId, relationshipId, editVendor]);

  const individualsMap = Object.fromEntries(
    contactIndividuals.map(({ id: indId, ...rest }) => [
      indId,
      { id: indId, ...rest }
    ])
  );

  const resolvedVendorContacts = (
    resolvedVendorRelDoc?.vendorContacts || []
  ).map((contact) => ({
    ...contact,
    ...(contact.individualId && individualsMap[contact.individualId]
      ? {
          name: individualsMap[contact.individualId].name,
          email: individualsMap[contact.individualId].email
        }
      : {})
  }));

  const onSelectVendor = (newVendor) => {
    setEditVendor(false);
    return setProductVendor(newVendor);
  };

  const [dbProducts] = useCachedDisplayData("product-components");

  const allLinkedProducts = resolvedVendorRelDoc
    ? dbProducts
        .filter((product) =>
          isProductLinkedToVendor(
            product,
            resolvedVendorRelDoc,
            productLifecycleEvents
          )
        )
        .map((product) => {
          const lifecycle =
            productLifecycleEvents.find(
              ({ productId }) => productId === product.id
            ) || {};
          return calculateMergedProductFootprint(
            product,
            lifecycle,
            emissionsFactors
          );
        })
    : [];

  const linkedProducts = [
    ...allLinkedProducts.filter(({ id }) => id === productId),
    ...allLinkedProducts.filter(({ id }) => id !== productId)
  ];

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        {showNewVendorForm && (editVendor || !vendorName) ? (
          <NewVendorForm
            setVendor={onSelectVendor}
            setShowNewVendorForm={setShowNewVendorForm}
            transactionSubcategory="spend-based"
          />
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="space-between"
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item xs={10}>
              {editVendor || !vendorName ? (
                <Grid container alignItems="center" wrap="nowrap" spacing={1}>
                  <Grid item xs>
                    <DbAutocomplete
                      availableOptions={vendors}
                      setValue={onSelectVendor}
                      label="Assign a vendor to this product"
                    />
                  </Grid>
                  <Grid item>
                    <Tooltip title="Add a new vendor">
                      <IconButton onClick={() => setShowNewVendorForm(true)}>
                        <AddCircleIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              ) : (
                <Typography variant="h4">{vendorName}</Typography>
              )}
            </Grid>
            {vendorName && (
              <Grid item>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {!editVendor && (
                    <>
                      <Tooltip title="View vendor">
                        <IconButton
                          onClick={() =>
                            setSelectedVendor({
                              ...resolvedVendorRelDoc,
                              linkedProducts: allLinkedProducts
                            })
                          }
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove vendor">
                        <IconButton onClick={onRemoveVendor}>
                          <LinkOffIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  <IconButton
                    onClick={() => {
                      setShowNewVendorForm(false);
                      setEditVendor((currentSetVendor) => !currentSetVendor);
                    }}
                  >
                    {editVendor ? <CancelIcon /> : <EditIcon />}
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      {vendorName && !editVendor && (
        <Grid item>
          <DataSharingTab
            relationshipId={relationshipId}
            isStripeCustomer={isStripeCustomer}
            isCheckingSubscription={isCheckingSubscription}
            vendorSurveys={vendorSurveys}
            surveyData={resolvedVendorRelDoc?.surveyData}
            linkedProducts={linkedProducts}
            currentProductId={productId}
            vendorContacts={resolvedVendorContacts}
            vendorStartDate={vendorStartDate}
            hideEmissionsFactors
          />
        </Grid>
      )}
    </Grid>
  );
};

const PlatformProductDetailsSlider = ({
  selectedProduct,
  setSelectedProduct,
  otherProducts,
  setReportProduct,
  setSelectedVendor
}) => {
  const {
    id,
    lifecycleId,
    kgsCo2e: originalKgsCo2e,
    footprintBreakdownKgs: originalFootprintBreakdownKgs,
    vendor: originalVendor,
    vendorRelationshipId,
    bomStatus,
    buyerIds = [],
    vendorIds = []
  } = selectedProduct;

  const [{ id: viewerAccountId }] = useAccountData();
  const isViewerBuyer = buyerIds.includes(viewerAccountId);
  const isViewerVendor = !isViewerBuyer && vendorIds.includes(viewerAccountId);

  const { vendors } = useEmissionsContext();
  const { activateSnackbar } = usePlatformLayoutContext();
  const { canEdit } = usePermissions();
  const resolvedOriginalVendor =
    (originalVendor?.id ? originalVendor : null) ||
    vendors.find(({ id }) => id === vendorRelationshipId) ||
    null;
  const { id: originalVendorId } = resolvedOriginalVendor || {};

  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const [productVendor, setProductVendor] = useState(resolvedOriginalVendor);
  const vendorId = productVendor?.vendorId || productVendor?.id || null;
  const vendorName = productVendor?.vendorName || productVendor?.name || "";

  useEffect(() => {
    if (!productVendor && vendorRelationshipId) {
      const resolved = vendors.find(({ id }) => id === vendorRelationshipId);
      if (resolved) {
        setProductVendor(resolved);
      }
    }
  }, [vendors, productVendor, vendorRelationshipId]);

  const onOpenReport = () => {
    setReportProduct(selectedProduct);
    return setSelectedProduct(null);
  };

  const onDownloadDataSheet = () =>
    exportProductsGapWorkbook([selectedProduct], {
      fileName: `${selectedProduct.sku || selectedProduct.name || "product"}-data-gaps.xlsx`
    }).catch((err) => {
      console.error("Product gap export failed:", err);
      activateSnackbar({
        message:
          "Couldn't generate the data-gap workbook. Please try again or contact support.",
        severity: "error"
      });
    });

  const onRemoveVendor = async () => {
    await Promise.all([
      updateCollectionDoc("product-lifecycle-events", lifecycleId, {
        vendor: firebaseFieldDeleteObj(),
        bomStatus: firebaseFieldDeleteObj()
      }),
      updateCollectionDoc("product-components", id, { vendorIds: [] })
    ]);
    return setProductVendor(null);
  };

  const additionalTabs = [
    {
      label: "Website",
      value: "website",
      content: <WebsiteTab {...selectedProduct} />
    },
    ...(isViewerVendor
      ? []
      : [
          {
            label: "Vendor",
            value: "vendor",
            content: (
              <VendorTab
                productVendor={productVendor}
                setProductVendor={setProductVendor}
                onRemoveVendor={onRemoveVendor}
                bomStatus={bomStatus}
                lifecycleEventId={lifecycleId}
                productId={id}
                setSelectedVendor={setSelectedVendor}
              />
            )
          }
        ])
  ];

  const onDeleteProduct = () => {
    updateCollectionDoc("product-components", id, { archived: true });
    return setSelectedProduct(null);
  };

  const productForDisplay = selectedProduct;

  return (
    <ProductDetailsSlider
      selectedProduct={productForDisplay}
      setSelectedProduct={setSelectedProduct}
      headerAction={
        <Tooltip title="Download this product's data sheet (missing data + assumptions)">
          <IconButton onClick={onDownloadDataSheet} size="small">
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
      }
      additionalTabs={additionalTabs}
      LeftContentComponent={
        !bomStatus || bomStatus === "confirmed" || !productVendor
          ? ProductFootprintLeftContent
          : undefined
      }
      leftContentProps={{
        onOpenReport,
        originalFootprintBreakdownKgs,
        originalKgsCo2e
      }}
      onDelete={isViewerVendor ? undefined : onDeleteProduct}
      additionalIsProductDifferent={vendorId !== originalVendorId}
      additionalLifecyleProps={{
        vendor: vendorId
          ? { name: vendorName, id: vendorId }
          : firebaseFieldDeleteObj(),
        ...(vendorId && !bomStatus
          ? { bomStatus: "pending" }
          : { bomStatus: vendorId ? bomStatus : firebaseFieldDeleteObj() })
      }}
      additionalProductProps={{ vendorIds: vendorId ? [vendorId] : [] }}
      otherProducts={otherProducts}
      isInApp
      pendingNote={
        isViewerBuyer &&
        bomStatus &&
        bomStatus !== "confirmed" &&
        !!productVendor
          ? "This product's data is awaiting input from the vendor."
          : isViewerVendor && bomStatus === "pending"
            ? "The customer assigned you as the vendor but hasn't requested this product's BOM yet. You'll be able to fill it in once they request it from their Vendors page."
            : isViewerVendor && bomStatus === "requested"
              ? "The customer is requesting this product's BOM data. Save your changes here, then toggle 'Share' on the customer's Data Sharing tab."
              : undefined
      }
      readOnly={
        !canEdit ||
        (isViewerBuyer && !!bomStatus && !!productVendor) ||
        (isViewerVendor &&
          bomStatus !== "requested" &&
          bomStatus !== "confirmed")
      }
      disabled={!canEdit}
    />
  );
};
export default PlatformProductDetailsSlider;
